"use client"

import { createContext, useContext, useEffect, useState } from "react"
import type { FieldValues } from "react-hook-form"
import { getMediaUploadStatus, type MediaOwner } from "@/services/offlineMediaService"
import { useAuthStore } from "@/store/authStore"

export class CaptureCoordinator {
  private pending = new Set<Promise<unknown>>()
  private failures = new Map<string, () => Promise<unknown>>()
  private closed = false
  isClosed = () => this.closed
  async settleForDiscard() {
    this.closed = true
    while (this.pending.size) await Promise.allSettled([...this.pending])
    this.failures.clear()
  }
  close() { this.closed = true }
  hasPendingCapture = () => this.pending.size > 0
  hasUnsafeCapture = () => this.hasPendingCapture() || this.failures.size > 0
  discardFailure(key: string) { this.failures.delete(key) }
  onFailure: (error: unknown) => void = () => undefined
  async protect(key: string, operation: () => Promise<unknown>) {
    if (this.closed) throw new Error("Esta sessão de captura está encerrada.")
    this.failures.set(key, operation)
    try { await this.run(operation); this.failures.delete(key) }
    catch (error) { this.onFailure(error); throw error }
  }
  async retryFailures() {
    for (const [key, operation] of this.failures) await this.protect(key, operation)
  }
  readValues: () => FieldValues = () => ({})
  checkpoint: () => Promise<unknown> = async () => undefined
  constructor(public readonly owner: MediaOwner) {}
  async run<T>(operation: () => Promise<T>): Promise<T> {
    if (this.closed) throw new Error("Esta sessão de captura está encerrada.")
    const pending = Promise.resolve().then(operation)
    this.pending.add(pending)
    try { return await pending } finally { this.pending.delete(pending) }
  }
  async flush() {
    while (this.pending.size) await Promise.all([...this.pending])
    if (this.failures.size) throw new Error("Não foi possível salvar uma captura. Tente salvar novamente antes de sair deste formulário.")
    await this.checkpoint()
    return structuredClone(this.readValues())
  }
  forField(fieldPath: string): MediaOwner { return { ...this.owner, fieldPath } }
}

export const activeCaptureCoordinators = new Set<CaptureCoordinator>()
export async function flushActiveCaptures() {
  for (const coordinator of activeCaptureCoordinators) if (!coordinator.isClosed()) await coordinator.flush()
}
export const SubmissionMediaContext = createContext<CaptureCoordinator | null>(null)
export const useCaptureCoordinator = () => useContext(SubmissionMediaContext)

export function MediaSyncStatus({ source }: { source?: string | { url: string; localMediaId?: string } | null }) {
  const url = typeof source === "string" ? source : source?.url
  const id = typeof source === "object" && source?.localMediaId ? source.localMediaId
    : url?.startsWith("offline-media://") ? url.slice("offline-media://".length) : undefined
  const userId = useAuthStore(state => state.user?.id)
  const [state, setState] = useState<{ uploaded: boolean; uploading: boolean; error?: string }>()
  useEffect(() => {
    let disposed = false
    setState(undefined)
    if (!id || !userId) return
    const refresh = () => {
      void getMediaUploadStatus(id).then(value => { if (!disposed) setState(value) })
        .catch(error => { if (!disposed) setState({ uploaded: false, uploading: false, error: String(error) }) })
    }
    refresh()
    window.addEventListener("dkro-media-changed", refresh)
    return () => { disposed = true; window.removeEventListener("dkro-media-changed", refresh) }
  }, [id, userId])
  if (!id || !userId || state?.uploaded) return null
  return <div className="text-xs" role={state?.error ? "alert" : "status"}>
    <span>{state?.uploading ? "Processando/enviando; a prévia local continua disponível."
      : state?.error ? `${state.error} Pause ou envie para tentar novamente.`
      : "Salvo neste aparelho. O envio começa quando você pausar ou enviar."}</span>
  </div>
}
